import { Routes } from '@angular/router';
import { authGuard } from './guards/auth/auth.guard';
import { FavoritesComponent } from './project/favorites/favorites.component';
import { quizUnsavedChangesGuard } from './guards/quiz-unsaved-changes/quiz-unsaved-changes.guard';
import { HasPermissionsGuard } from './guards/has-permission/has-permission.guard';
import { PermissionsEnum } from './enums/permissions.enum';
import { SettingsAccountComponent } from './user/settings-account/settings-account.component';
import { SettingsPasswordComponent } from './user/settings-password/settings-password.component';
import { SettingsPaymentMethodComponent } from './user/settings-payment-method/settings-payment-method.component';
import { SettingsPurchaseHistoryComponent } from './user/settings-purchase-history/settings-purchase-history.component';
import { settingsChangesGuard } from './guards/settings-changes/settings-changes.guard';
import { userDataResolver } from './resolvers/userData.resolver';
import { orderResolver } from './resolvers/order.resolver';
import { orderProcessingGuard } from './guards/order-processing/order-processing.guard';
import { FootballLmsHomepageComponent } from './project/landing-page/homepage/homepage.component';
import { ssoGuard } from './guards/sso/sso.guard';
import { SsoWaitingComponent } from './shared/sso-waiting/sso-waiting.component';
import { ExpirationContext } from './enums/expirationContext.enum';
import { courseEditGuard } from './guards/course-edit/course-edit.guard';
import { EditCategoryGuard } from './guards/edit-category/edit-category.guard';
import { hasTokenGuard } from './guards/hasToken/has-token.guard';
import { saveChangesGuard } from './guards/save-changes/save-changes.guard';
import { maintenanceGuard } from './guards/maintenance/maintenance.guard';
import { RouteEnum } from './enums/routes.enum';
import { embedTokenGuard } from './guards/embed-token/embed-token.guard';
import { EmbedTokenType } from './enums/embedTokenType.enum';

export const routes: Routes = [
	{
		path: 'login',
		loadComponent: () => import('./auth/login/login.component').
			then(m => m.LoginComponent),
		canActivate: [authGuard, ssoGuard],
		data: { onlyLoggedOut: true },
		pathMatch: 'full',
	},
	{
		path: 'maintenance',
		loadComponent: () => import('./shared/maintenance/maintenance.component').
			then(m => m.MaintenanceComponent),
		canActivate: [maintenanceGuard],
		pathMatch: 'full'
	},
	{
		path: 'forgot-password',
		loadComponent: () => import('./auth/forgot-password/forgot-password.component').
			then(m => m.ForgotPasswordComponent),
		pathMatch: 'full',
	},
	{
		path: 'reset-password',
		loadComponent: () => import('./auth/reset-password/reset-password.component').
			then(m => m.ResetPasswordComponent),
		pathMatch: 'full',
		canActivate: [hasTokenGuard],
	},
	{
		path: 'home',
		component: FootballLmsHomepageComponent,
		pathMatch: 'prefix',
		canActivate: [authGuard, HasPermissionsGuard],
		data: { breadcrumb: 'Home', onlyLoggedOut: true, permission: [PermissionsEnum.VIEW_HOME] },
	},
	{
		path: 'sso',
		component: SsoWaitingComponent
	},
	{
		path: 'embed/chat',
		loadComponent: () => import('./shared/chat-embed/chat-embed.component').
			then(m => m.ChatEmbedComponent),
		pathMatch: 'full',
		canActivate: [embedTokenGuard],
		data: { embedTokenType: EmbedTokenType.CHAT }
	},
	{
		path: 'embed/activity',
		loadComponent: () => import('./shared/activity-embed/activity-embed.component').
			then(m => m.ActivityEmbedComponent),
		pathMatch: 'full',
		canActivate: [embedTokenGuard],
		data: { embedTokenType: EmbedTokenType.ACTIVITY }
	},
	{
		path: 'home/shopping-cart',
		loadComponent: () => import('./shared/outlet/outlet.component').
			then(m => m.OutletComponent),
		data: { breadcrumb: '', onlyLoggedOut: true, permission: [PermissionsEnum.VIEW_SHOPPING_CART] },
		canActivate: [authGuard, HasPermissionsGuard],
		children: [
			{
				path: '',
				loadComponent: () => import('./project/shopping-cart/shopping-cart.component').
					then(m => m.ShoppingCartComponent)
			}
		]
	},
	{
		path: '',
		loadComponent: () => import('./project/main/main.component').
			then(m => m.MainComponent),
		canActivate: [authGuard],
		data: { breadcrumb: '', onlyLoggedOut: false },
		children: [
			{
				path: 'category-creation',
				children: [

						{
							path: '',
							loadComponent: () => import('./project/category-creation-v3/category-creation-v3-page.component').
							then(m => m.CategoryCreationV3PageComponent),
							data: { breadcrumb: 'Category Creation', permission: [PermissionsEnum.CREATE_CATEGORY, PermissionsEnum.EDIT_CATEGORY] },
							canActivate: [
								HasPermissionsGuard
							],
							canDeactivate: [
								saveChangesGuard
							]
						},
						{
							path: ':id/translations',
							loadComponent: () => import('./project/category-creation-v2/translations-page/category-translations-page.component').
								then(m => m.CategoryTranslationsPageComponent),
							data: { permission: PermissionsEnum.EDIT_CATEGORY },
							canActivate: [
								HasPermissionsGuard,
								EditCategoryGuard
							],
							canDeactivate: [
								saveChangesGuard
							]
						},
						{
							path: ':id',
							loadComponent: () => import('./project/category-creation-v3/category-creation-v3-page.component').
								then(m => m.CategoryCreationV3PageComponent),
							data: { permission: PermissionsEnum.EDIT_CATEGORY },
							canActivate: [
								HasPermissionsGuard,
								EditCategoryGuard
							],
							canDeactivate: [
								saveChangesGuard
							]
						},
					],

			},
			{
				path: 'categories',
				children: [
					// {
					// 	path: '',
					// 	loadComponent: () => import('./project/category-list/category-list.component').
					// 	then(m => m.CategoryListComponent),
					// 	data: { breadcrumb: 'Categories', permission: PermissionsEnum.VIEW_CATEGORY },
					// 	canActivate: [
					// 		HasPermissionsGuard
					// 	],
					// },
					{
						path: '',
						loadComponent: () => import('./project/category-list-v2/category-list-v2.component').
						then(m => m.CategoryListV2Component),
						data: { breadcrumb: { skip: true }, permission: PermissionsEnum.VIEW_CATEGORY },
						canActivate: [
							HasPermissionsGuard
						],
					},
					{
						path: 'course-preview',
						data: { breadcrumb: { skip: true }, permission: PermissionsEnum.VIEW_COURSE },
						canActivate: [
							HasPermissionsGuard
						],
						children: [
							{
								path: ':categoryId',
								data: { breadcrumb: { alias: 'categoryName', info: 'categoryId', routeInterceptor: () => {
										return '/categories';
									}
								},
								permission: PermissionsEnum.VIEW_COURSE },
								canActivate: [
									HasPermissionsGuard
								],
								children: [
									{
										path: ':courseId',
										loadComponent: () => import('./project/chapter-preview/chapter-preview.component').
											then(m => m.ChapterPreviewComponent),
											data: { breadcrumb: { alias: 'courseName', info: 'courseId', routeInterceptor: () => {
												return '/categories';
											}
										}, permission: PermissionsEnum.VIEW_COURSE },
										canActivate: [
											HasPermissionsGuard
										],
									}
								]
							}
						]
					},
				]
			},
			{
				path: 'explore',
				children: [
					{
						path: '',
						loadComponent: () => import('./project/explore/explore.component').
						then(m => m.ExploreComponent),
						data: { breadcrumb: 'Explore', permission: PermissionsEnum.VIEW_EXPLORE },
						canActivate: [
							HasPermissionsGuard
						],
					},
				]
			},
			// {
			// 	path: 'courses',
			// 	loadComponent: () => import('./project/courses/courses.component').
			// 		then(m => m.CoursesComponent),
			// 	data: { breadcrumb: 'courses', permission: PermissionsEnum.VIEW_COURSE },
			// 	canActivate: [
			// 		HasPermissionsGuard
			// 	]
			// },
			{
				path: 'courses',
				loadComponent: () => import('./project/courses-v2/courses-v2.component').
					then(m => m.CoursesV2Component),
				data: { breadcrumb: { skip: true }, permission: PermissionsEnum.VIEW_COURSE },
				canActivate: [
					HasPermissionsGuard
				]
			},
			{
				path: 'favorites',
				component: FavoritesComponent,
				data: { breadcrumb: 'favorites', permission: PermissionsEnum.VIEW_FAVORITES },
				canActivate: [
					HasPermissionsGuard
				]
			},
			{
				path: RouteEnum.COURSE_CREATION,
				loadComponent: () =>
				import('./project/course-creation-v3/course-creation-v3-page.component')
					.then(m => m.CourseCreationV3PageComponent),
				data: { permission: PermissionsEnum.CREATE_COURSE },
				canActivate: [HasPermissionsGuard],
				canDeactivate: [saveChangesGuard],
			},
			{
				path: `${RouteEnum.COURSE_CREATION}/:id`,
				loadComponent: () =>
				import('./project/course-creation-v3/course-creation-v3-page.component')
					.then(m => m.CourseCreationV3PageComponent),
				data: { permission: PermissionsEnum.EDIT_COURSE },
				canActivate: [HasPermissionsGuard, courseEditGuard],
				canDeactivate: [saveChangesGuard],
			},
			{
				path: 'category-overview',
				children: [
					{
						path: '',
						loadComponent: () => import('./project/category-overview/category-overview.component').
						then(m => m.CategoryOverviewComponent),
						canActivate: [
							HasPermissionsGuard
						],
					},
					{
						path: ':id',
						loadComponent: () => import('./project/category-overview/category-overview.component').
								then(m => m.CategoryOverviewComponent),
						data: { permission: PermissionsEnum.EDIT_COURSE },
						canActivate: [
							HasPermissionsGuard
						]
					},
				],

			},
			{
				path: 'my-courses',
				children: [
					{
						path: '',
						loadComponent: () => import('./project/my-courses/my-courses.component').
							then(m => m.MyCoursesComponent),
						data: { breadcrumb: { skip: true }, permission: PermissionsEnum.VIEW_MY_COURSES },
						canActivate: [
							HasPermissionsGuard
						]
					},
					{
						path: ':categoryId',
						data: { breadcrumb: { alias: 'categoryName', routeInterceptor: () => {
							return '/my-courses';
							}
						},
						permission: PermissionsEnum.VIEW_MY_COURSES },
						canActivate: [
							HasPermissionsGuard
						],
						children: [
							{
								path: 'jotForm',
								loadComponent: () => import('./project/jotform-embedded/jotform-embedded.component').
									then(m => m.JotformEmbeddedComponent),
								data: { breadcrumb: 'Form' }
							},
						]
					},
					{
						path: 'chapter-view',
						data: { breadcrumb: { skip: true }, permission: PermissionsEnum.VIEW_MY_COURSES },
						canActivate: [
							HasPermissionsGuard
						],
						children: [
							{
								path: ':categoryId',
								data: { breadcrumb: { alias: 'categoryName' }, permission: PermissionsEnum.VIEW_MY_COURSES },
								canActivate: [
									HasPermissionsGuard
								],
								children: [
									{
										path: '',
										redirectTo: '/my-courses',
										pathMatch: 'full'
									},
									{
										path: ':courseId',
										loadComponent: () => import('./project/chapter-view/chapter-view.component').
											then(m => m.ChapterViewComponent),
										data: { breadcrumb: { alias: 'courseName' }, permission: PermissionsEnum.VIEW_MY_COURSES },
										canActivate: [
											HasPermissionsGuard
										],
										canDeactivate: [quizUnsavedChangesGuard],
									}
								]
							}
						]
					},
				]
			},
			// {
			// 	path: 'my-media-library',
			// 	data: { breadcrumb: 'My Media Library', permission: PermissionsEnum.VIEW_VIDEO },
			// 	canActivate: [
			// 		HasPermissionsGuard
			// 	],
			// 	loadComponent: () => import('./project/my-media-library/my-media-library.component').
			// 		then(m => m.MyMediaLibraryComponent),
			// },
			{
				path: 'my-media-library',
				data: { breadcrumb: { skip: true }, permission: PermissionsEnum.VIEW_VIDEO },
				canActivate: [
					HasPermissionsGuard
				],
				loadComponent: () => import('./project/my-media-library-v2/my-media-library-v2.component').
					then(m => m.MyMediaLibraryV2Component),
			},
			{
				path: 'my-notes',
				data: { breadcrumb: 'My Notes', permission: PermissionsEnum.VIEW_NOTE },
				canActivate: [
					HasPermissionsGuard
				],
				loadComponent: () => import('./project/my-notes/my-notes.component').
					then(m => m.MyNotesComponent),
			},
			{
				path: 'manage-users',
				data: { breadcrumb: 'Manage Users', permission: PermissionsEnum.VIEW_STUDENT },
				canActivate: [
					HasPermissionsGuard
				],
				loadComponent: () => import('./project/manage-users/manage-users.component').
					then(m => m.ManageUsersComponent),
			},
			{
				path: 'manage-clients',
				data: { breadcrumb: { skip: true }, permission: PermissionsEnum.VIEW_CLIENT },
				canActivate: [
					HasPermissionsGuard
				],
				loadComponent: () => import('./project/manage-clients-v2/manage-clients-v2.component').
					then(m => m.ManageClientsV2Component),
			},
			{
				path: 'webhooks',
				data: { breadcrumb: 'Webhooks', permission: PermissionsEnum.VIEW_WEBHOOKS },
				canActivate: [
					HasPermissionsGuard
				],
				loadComponent: () => import('./project/webhooks/webhooks.component').
					then(m => m.WebhooksComponent),
			},
			{
				path: 'shopping-cart',
				loadComponent: () => import('./project/shopping-cart/shopping-cart.component').
					then(m => m.ShoppingCartComponent),
				canActivate: [
					HasPermissionsGuard
				],
				data: { breadcrumb: 'Shopping Cart', permission: PermissionsEnum.VIEW_SHOPPING_CART },
				pathMatch: 'prefix',
			},
			{
				path: 'permissions',
				loadComponent: () => import('./project/permissions/permissions.component').
					then(m => m.PermissionsComponent),
				canActivate: [
					HasPermissionsGuard
				],
				data: { breadcrumb: 'Permissions', permission: PermissionsEnum.VIEW_PERMISSIONS }
			},
			{
				path: 'sales',
				canActivate: [
					HasPermissionsGuard
				],
				data: { breadcrumb: 'Sales', permission: PermissionsEnum.VIEW_SALES},
				children: [
					{
						path: '',
						loadComponent: () => import('./project/sales/sales.component').
							then(m => m.SalesComponent)
					},
					{
						path: 'products',
						loadComponent: () => import('./project/product-sales/product-sales.component')
							.then(m => m.ProductSalesComponent)
					},
					{
						path: 'orders',
						loadComponent: () => import('./project/orders-table/orders-table.component')
							.then(m => m.OrdersTableComponent)
					},
				]
			},
			{
				path: 'roles',
				loadComponent: () => import('./project/roles/roles.component').
					then(m => m.RolesComponent),
				canActivate: [
					HasPermissionsGuard
				],
				data: { breadcrumb: 'Roles', permission: PermissionsEnum.VIEW_ROLES }
			},
			{
				path: RouteEnum.ONLINE_USERS,
				loadComponent: () => import('./project/online-users/online-users.component').
					then(m => m.OnlineUsersComponent),
				canActivate: [
					HasPermissionsGuard
				],
				data: { breadcrumb: 'Online users', permission: PermissionsEnum.VIEW_ONLINE_USERS }
			},
			{
				path: 'chatbot-messages',
				canActivate: [
					HasPermissionsGuard
				],
				data: { breadcrumb: 'Chatbot messages', permission: PermissionsEnum.VIEW_CHATBOT_MESSAGES },
				children: [
					{
						path: '',
						loadComponent: () => import('./project/chatbot-messages/chatbot-messages.component').
							then(m => m.ChatbotMessagesComponent),
					},
					{
						path: ':userId',
						loadComponent: () => import('./project/chatbot-messages/chatbot-conversation/chatbot-conversation.component').
							then(m => m.ChatbotConversationComponent),
						data: { breadcrumb: { skip: true } },
					},
				]
			},
			{
				path: 'order/:orderId',
				loadComponent: () => import('./user/order-processing/order-processing.component').
					then(m => m.OrderProcessingComponent),
				canActivate: [HasPermissionsGuard],
				data: { permission: [PermissionsEnum.VIEW_ORDER] },
				resolve: {
					order: orderResolver
				},
				canDeactivate: [orderProcessingGuard]
			},
			{
				path: 'account',
				canActivate: [
					authGuard,
					HasPermissionsGuard
				],
				data: { breadcrumb: 'Account', permission: [PermissionsEnum.VIEW_ACCOUNT_SETTINGS], onlyLoggedOut: false },
				children: [
					{
						path: '',
						children: [
							{
								path: 'settings',
								loadComponent: () => import('./user/settings/settings.component').
									then(m => m.SettingsComponent),
								resolve: {
									userData: userDataResolver
								},
								data: { permission: [PermissionsEnum.VIEW_ACCOUNT_SETTINGS] },
								canActivate: [HasPermissionsGuard],
								children: [
									{
										path: '',
										component: SettingsAccountComponent,
										canDeactivate: [settingsChangesGuard],
										canActivate: [HasPermissionsGuard],
										data: { permission: [PermissionsEnum.VIEW_ACCOUNT_SETTINGS] },
									},
									{
										path: 'password',
										component: SettingsPasswordComponent,
										canDeactivate: [settingsChangesGuard],
										canActivate: [HasPermissionsGuard],
										data: { permission: [PermissionsEnum.VIEW_ACCOUNT_SETTINGS_PASSWORD] },
									},
									{
										path: 'payment-method',
										component: SettingsPaymentMethodComponent,
										canDeactivate: [settingsChangesGuard],
										canActivate: [HasPermissionsGuard],
										data: { permission: [PermissionsEnum.VIEW_ACCOUNT_SETTINGS_PAYMENT_METHODS] },
									},
									{
										path: 'purchase-history',
										component: SettingsPurchaseHistoryComponent,
										canDeactivate: [settingsChangesGuard],
										canActivate: [HasPermissionsGuard],
										data: { permission: [PermissionsEnum.VIEW_ACCOUNT_SETTINGS_PURCHASE_HISTORY] },
									}
								]
							}
						]
					}
				]
			},
		]
	},
	{
		path: 'home/category-overview',
		loadComponent: () => import('./project/main/main.component').
			then(m => m.MainComponent),
		canActivate: [authGuard, HasPermissionsGuard],
		data: { breadcrumb: '', onlyLoggedOut: true, permission: [PermissionsEnum.VIEW_SHOPPING_CART] },
		children: [
			{
				path: '',
				loadComponent: () => import('./project/category-overview/category-overview.component').
					then(m => m.CategoryOverviewComponent),
				pathMatch: 'prefix',
			},
		]
	},
	{
		path: 'terms-and-conditions',
		loadComponent: () => import('./shared/terms-and-conditions/terms-and-conditions.component').
			then(m => m.TermsAndConditionsComponent),
		canActivate: [HasPermissionsGuard],
		data: { permission: [PermissionsEnum.VIEW_TERMS_AND_CONDITIONS] },
		pathMatch: 'full',
	},
	{
		path: 'privacy-policy',
		loadComponent: () => import('./shared/privacy-policy/privacy-policy.component').
			then(m => m.PrivacyPolicyComponent),
		canActivate: [HasPermissionsGuard],
		data: { permission: [PermissionsEnum.VIEW_PRIVACY_POLICY] },
		pathMatch: 'full',
	},
	{
		path: 'cookie-notice',
		loadComponent: () => import('./shared/cookie-notice/cookie-notice.component').
			then(m => m.CookieNoticeComponent),
		canActivate: [HasPermissionsGuard],
		data: { permission: [PermissionsEnum.VIEW_COOKIE_NOTICE] },
		pathMatch: 'full',
	},
	{
		path: 'refund-and-delivery-policy',
		loadComponent: () => import('./shared/refund-and-delivery/refund-and-delivery.component').
			then(m => m.RefundAndDeliveryComponent),
		canActivate: [HasPermissionsGuard],
		data: { permission: [PermissionsEnum.VIEW_REFUND_AND_DELIVERY_POLICY] },
		pathMatch: 'full',
	},
	{
		path: 'link-expired',
		loadComponent: () => import('./shared/expiration/expiration.component').
			then(m => m.ExpirationComponent),
		data: { context: ExpirationContext.SSO_LINK },
		pathMatch: 'full',
	},
	{
		path: 'embed-token-expired',
		loadComponent: () => import('./shared/expiration/expiration.component').
			then(m => m.ExpirationComponent),
		data: { context: ExpirationContext.EMBED_TOKEN },
		pathMatch: 'full',
	},
	{
		path: '**',
		loadComponent: () => import('./shared/not-found/not-found.component').
			then(m => m.NotFoundComponent),
		canActivate: [authGuard],
		pathMatch: 'full',
	},

];
